import { useTranslation } from "react-i18next";
import { InvoiceDto } from "@/types/api/api-types";
import { createColumnHelper } from "@tanstack/react-table";
import FileIcon from "@/assets/icons/file.svg?react";
import EyeIcon from "@/assets/icons/eye.svg?react";
import CheckIcon from "@/assets/icons/check.svg?react";
import XIcon from "@/assets/icons/x.svg?react";
import Trash from "@/assets/icons/trash.svg?react";
import { InvoicePaymentStatusBadge } from "@/components/shared/badge/invoicePaymentStatus.badge";
import { Dispatch, SetStateAction } from "react";

const columnHelper = createColumnHelper<InvoiceDto>();

const STATUS_EDIT_ROLES = ["SUPERADMIN", "ACCOUNTANT"];

export const useLitigeTableColumns = (
  displayDocument: (invoiceKey: string) => Promise<void>,
  setSelectedInvoice: Dispatch<SetStateAction<InvoiceDto | null>>,
  setInvoiceDeleteModal: Dispatch<SetStateAction<string | null>>,
  role: string | null,
) => {
  const { t } = useTranslation("boInvoices");
  return [
    columnHelper.display({
      id: "client",
      header: t("litigesTable.columns.client"),
      cell: (info) => info.row.original.from.businessName,
    }),
    columnHelper.display({
      id: "invoice",
      header: t("litigesTable.columns.invoice"),
      cell: (info) => (
        <div className="flex flex-row items-center gap-4">
          <FileIcon width={20} height={20} />
          <span className="text-sm font-medium text-gray-900">
            {t("litigesTable.litigeName", { invoiceId: info.row.original.invoiceId })}
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: "status",
      header: t("litigesTable.columns.status"),
      meta: `justify-center`,
      cell: (info) => <InvoicePaymentStatusBadge status={info.row.original.paymentStatus} />,
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <div className="flex flex-row items-center justify-center gap-3">
          <button
            className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
            onClick={() => displayDocument(info.row.original.invoiceKey)}
          >
            <EyeIcon width={20} height={20} color="#06F" />
          </button>
          {info.row.original.billingEntity.paymentMethod === "VIREMENT" && (
            <button
              className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
              onClick={() => setSelectedInvoice(info.row.original)}
            >
              {info.row.original.paymentStatus !== "PAID" ? (
                <CheckIcon width={20} height={20} color="#06F" />
              ) : (
                <XIcon width={20} height={20} color="#ff0000" />
              )}
            </button>
          )}
          {STATUS_EDIT_ROLES.includes(role || "") && (
            <button
              className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
              onClick={() => setInvoiceDeleteModal(info.row.original.id)}
            >
              <Trash width={20} height={20} color="#ff0000" />
            </button>
          )}
        </div>
      ),
    }),
  ];
};
